/*!
 * egt.tin.duns — business TIN (EIN) + DUNS field with opt-outs (app-scoped).
 *
 * Spec §12: "Business TIN or DUNS: [EIN][DUNS] + options: 'I want to pay
 * personally, this is a passion project.' and 'I don't have this information
 * at this moment.'"
 *
 * EIN  : US Employer ID Number, 9 digits, masked XX-XXXXXXX (2-7 grouping).
 * DUNS : 9 digits.
 * Two opt-out checkboxes. When either opt-out is checked the EIN/DUNS inputs
 * are disabled (and cleared from the saved payload).
 *
 * Renders raw DOM inputs (NOT nested <uae>) so it is one uae[name] boundary.
 *
 * save() -> { ein, duns, payPersonal, noInfo }   (ein/duns are normalized
 *           digit-only strings; the on-screen value is masked)
 * load() -> restores the same shape.
 */

var _ = require("../../uam/module.js")(module.exports, {
  namespace: "egt.tin.duns",
  title: "TIN / DUNS",
  description: "Business EIN and DUNS with personal-payment and no-info opt-outs.",
  control: true,
  preview: "egt.preview.tinduns",
  supportServer: false,
  fields: {
    name: {
      title: "Name",
      type: "string",
      description: "Form field name.",
      required: true
    },
    title: {
      title: "Title",
      type: "string",
      description: "Control title."
    },
    description: {
      title: "Description",
      type: "inner",
      description: "Helper text."
    },
    value: {
      title: "Value",
      type: "any",
      description: "Initial value object { ein, duns, payPersonal, noInfo }."
    }
  }
});

function digitsOnly(value, max) {
  var d = (value || "").toString().replace(/[^0-9]/g, "");
  if (typeof max === "number") d = d.slice(0, max);
  return d;
}

function maskEin(rawDigits) {
  var d = digitsOnly(rawDigits, 9);
  if (d.length <= 2) return d;
  return d.slice(0, 2) + "-" + d.slice(2);
}

function normalizeValue(value) {
  var v = (value && typeof value === "object") ? value : {};
  return {
    ein: digitsOnly(v.ein, 9),
    duns: digitsOnly(v.duns, 9),
    payPersonal: v.payPersonal === true,
    noInfo: v.noInfo === true
  };
}

exports.render = async function (options) {
  var name = (options.name || "").toString().trim();
  var title = (options.title || "").toString().trim();
  var description = (options.description || "").toString().trim();
  var initial = normalizeValue(options.value);

  var wrapper = document.createElement("div");
  wrapper.className = "form-group pb-3 egt-tinduns";
  wrapper.setAttribute("name", name);

  if (title) {
    var label = document.createElement("label");
    label.className = "form-label fw-semibold";
    label.textContent = title;
    wrapper.appendChild(label);
  }

  if (description) {
    var help = document.createElement("div");
    help.className = "form-text mb-2";
    help.textContent = description;
    wrapper.appendChild(help);
  }

  var grid = document.createElement("div");
  grid.className = "row g-2 mb-2 egt-tinduns__grid";

  // EIN
  var einCol = document.createElement("div");
  einCol.className = "col-12 col-lg-6";
  var einLabel = document.createElement("label");
  einLabel.className = "form-label small mb-1";
  einLabel.textContent = "EIN (Employer ID Number)";
  var einInput = document.createElement("input");
  einInput.type = "text";
  einInput.className = "form-control form-control-sm";
  einInput.setAttribute("data-tinduns-ein", "true");
  einInput.setAttribute("inputmode", "numeric");
  einInput.setAttribute("autocomplete", "off");
  einInput.placeholder = "12-3456789";
  einInput.maxLength = 10; // 9 digits + 1 separator
  einInput.value = maskEin(initial.ein);
  einCol.appendChild(einLabel);
  einCol.appendChild(einInput);

  // DUNS
  var dunsCol = document.createElement("div");
  dunsCol.className = "col-12 col-lg-6";
  var dunsLabel = document.createElement("label");
  dunsLabel.className = "form-label small mb-1";
  dunsLabel.textContent = "DUNS Number";
  var dunsInput = document.createElement("input");
  dunsInput.type = "text";
  dunsInput.className = "form-control form-control-sm";
  dunsInput.setAttribute("data-tinduns-duns", "true");
  dunsInput.setAttribute("inputmode", "numeric");
  dunsInput.setAttribute("autocomplete", "off");
  dunsInput.placeholder = "123456789";
  dunsInput.maxLength = 9;
  dunsInput.value = initial.duns;
  dunsCol.appendChild(dunsLabel);
  dunsCol.appendChild(dunsInput);

  grid.appendChild(einCol);
  grid.appendChild(dunsCol);
  wrapper.appendChild(grid);

  // Opt-outs
  function optOut(attrName, text, checked) {
    var box = document.createElement("div");
    box.className = "form-check egt-tinduns__optout";
    var input = document.createElement("input");
    input.type = "checkbox";
    input.className = "form-check-input";
    input.setAttribute(attrName, "true");
    input.checked = !!checked;
    var lbl = document.createElement("label");
    lbl.className = "form-check-label small";
    lbl.textContent = text;
    box.appendChild(input);
    box.appendChild(lbl);
    return box;
  }

  var payPersonalBox = optOut("data-tinduns-paypersonal", "I want to pay personally — this is a passion project.", initial.payPersonal);
  var noInfoBox = optOut("data-tinduns-noinfo", "I don't have this information at this moment.", initial.noInfo);
  wrapper.appendChild(payPersonalBox);
  wrapper.appendChild(noInfoBox);

  function syncDisabled() {
    var optedOut =
      (payPersonalBox.querySelector("input").checked) ||
      (noInfoBox.querySelector("input").checked);
    einInput.disabled = optedOut;
    dunsInput.disabled = optedOut;
    wrapper.classList.toggle("egt-tinduns--optedout", optedOut);
  }

  einInput.addEventListener("input", function () {
    einInput.value = maskEin(einInput.value);
  });
  dunsInput.addEventListener("input", function () {
    dunsInput.value = digitsOnly(dunsInput.value, 9);
  });
  payPersonalBox.querySelector("input").addEventListener("change", syncDisabled);
  noInfoBox.querySelector("input").addEventListener("change", syncDisabled);

  syncDisabled();
  return wrapper;
};

function collect(control) {
  var einInput = control.querySelector("[data-tinduns-ein]");
  var dunsInput = control.querySelector("[data-tinduns-duns]");
  var payPersonal = control.querySelector("[data-tinduns-paypersonal]");
  var noInfo = control.querySelector("[data-tinduns-noinfo]");
  return {
    ein: einInput ? digitsOnly(einInput.value, 9) : "",
    duns: dunsInput ? digitsOnly(dunsInput.value, 9) : "",
    payPersonal: !!(payPersonal && payPersonal.checked),
    noInfo: !!(noInfo && noInfo.checked)
  };
}

async function save(name, control, repo) {
  var data = collect(control);
  var optedOut = data.payPersonal || data.noInfo;
  var errors = [];

  control.classList.remove("is-invalid");

  if (!optedOut) {
    if (data.ein && data.ein.length !== 9) {
      errors.push({ type: "validation", message: "An EIN must be 9 digits (XX-XXXXXXX)." });
    }
    if (data.duns && data.duns.length !== 9) {
      errors.push({ type: "validation", message: "A DUNS number must be 9 digits." });
    }
  }

  if (errors.length > 0) {
    repo.success = false;
    repo.errors = errors;
    control.classList.add("is-invalid");
    repo.data = data;
    return repo;
  }

  // If opted out, do not store partial EIN/DUNS.
  if (optedOut) {
    data.ein = "";
    data.duns = "";
  }

  repo.success = true;
  repo.data = data;
  return repo;
}
module.exports.save = save;

async function load(name, control, data) {
  var v = normalizeValue(data);
  var einInput = control.querySelector("[data-tinduns-ein]");
  var dunsInput = control.querySelector("[data-tinduns-duns]");
  var payPersonal = control.querySelector("[data-tinduns-paypersonal]");
  var noInfo = control.querySelector("[data-tinduns-noinfo]");
  if (einInput) einInput.value = maskEin(v.ein);
  if (dunsInput) dunsInput.value = v.duns;
  if (payPersonal) payPersonal.checked = v.payPersonal;
  if (noInfo) noInfo.checked = v.noInfo;
  if (payPersonal) payPersonal.dispatchEvent(new Event("change", { bubbles: true }));
}
module.exports.load = load;
